/**
 * The one place a colour is decided.
 *
 * There used to be two: docs/design/tokens.css defined twenty CSS variables, and theme/antd-theme.ts typed the same
 * hex values out again for Ant Design. Change one and the other did not follow. Worse, seventy-one colours in the
 * stylesheet were literals no variable controlled — the whole sidebar, the login pane, every status chip — so swapping
 * the twenty would have moved about half the interface.
 *
 * A theme is now data: this file's shape, applied to `:root` as custom properties and handed to Ant Design's
 * ConfigProvider, so both layers move together and a theme can be swapped at runtime with no rebuild.
 *
 * The printed documents deliberately do NOT follow it. An RV, an F03/F04 tag and the letters are controlled formats
 * that ADPAD files on paper; they are black on white whatever the screen is wearing. Their rules live in the "documents
 * (paper)" section of tokens.css and in templates/documents/documents.css, and use literals on purpose.
 */
export interface Theme {
  /** what the theme is called in the picker */
  name: string;
  /** true when the surface is dark, so components that need to know can ask rather than guess from a colour */
  dark: boolean;
  colors: ThemeColors;
  /** corner radius in px; 0 gives square-edged, 8+ gives soft */
  radius: number;
  /** base body size in px — everything else is relative to it */
  fontSize: number;
  fontFamily: string;
  /** the Arabic stack, which needs a face that has the glyphs */
  fontFamilyAr: string;
}

export interface ThemeColors {
  // ---- brand ----
  /** the department's navy: sidebar, headings, primary surfaces */
  navy: string;
  navy2: string;
  navy3: string;
  /** the gold ADPAD signs with: primary buttons, focus rings, the active tab */
  gold: string;
  gold2: string;
  goldHover: string;
  /** text that sits on gold — dark, because gold is a light surface */
  onGold: string;

  // ---- surfaces and ink ----
  bg: string;
  paper: string;
  ink: string;
  muted: string;
  line: string;
  line2: string;

  // ---- the sidebar, which is its own small world ----
  /** the sidebar's own surface: the department's navy, or the platform's light grey */
  sbBg: string;
  /** the hairlines inside the sidebar (logo block, user block) */
  sbLine: string;
  /** the background of the active item */
  sbActive: string;
  sbText: string;
  sbTextDim: string;
  sbTextStrong: string;
  sbHeading: string;
  sbHover: string;

  // ---- condition and status, the colours the store actually reasons in ----
  svc: string;
  svcBg: string;
  svcInk: string;
  svcBorder: string;
  us: string;
  usBg: string;
  usInk: string;
  usBorder: string;
  quar: string;
  quarBg: string;
  quarInk: string;
  quarBorder: string;
  /** the softer gold tint behind a selected option and the "gold" chip */
  goldBg: string;
  info: string;
  infoBg: string;
  infoInk: string;
  infoBorder: string;

  // ---- tables and forms ----
  rowHead: string;
  rowHover: string;
  rowAlt: string;
  rowBad: string;
  rowWarn: string;
  fieldBorder: string;
  placeholder: string;

  /** the phone frame in scan mode, which is near-black whatever the theme */
  scanBg: string;
}

/** ADPAD's own colours — the signed-off look, and the default every other theme starts from. */
export const ADPAD: Theme = {
  name: 'ADPAD Navy',
  dark: false,
  radius: 6,
  fontSize: 13.5,
  fontFamily: '"Segoe UI", system-ui, -apple-system, Roboto, Arial, sans-serif',
  fontFamilyAr: '"Segoe UI", Tahoma, "Noto Naskh Arabic", Arial, sans-serif',
  colors: {
    navy: '#10284B',
    navy2: '#173A6B',
    navy3: '#0B1C36',
    gold: '#C8A24A',
    gold2: '#A98632',
    goldHover: '#D9B45A',
    onGold: '#1A1400',

    bg: '#F3F5F8',
    paper: '#FFFFFF',
    ink: '#172033',
    muted: '#5B6474',
    line: '#D9DEE6',
    line2: '#EEF1F5',

    sbBg: '#10284B',
    sbLine: 'rgba(255,255,255,.08)',
    sbActive: 'rgba(255,255,255,.08)',
    sbText: '#C9D3E3',
    sbTextDim: '#9FB0CA',
    sbTextStrong: '#FFFFFF',
    sbHeading: '#B9C6DA',
    sbHover: 'rgba(255,255,255,.08)',

    svc: '#2F9E44',
    svcBg: '#E7F6EA',
    svcInk: '#1D6B2E',
    svcBorder: '#E7F6EA',
    us: '#D7301F',
    usBg: '#FBE9E7',
    usInk: '#9E2216',
    usBorder: '#F1B8B2',
    quar: '#D98E04',
    quarBg: '#FFF3DC',
    quarInk: '#8A5A00',
    quarBorder: '#F3D9A4',
    goldBg: '#FBF3DC',
    info: '#2B6CB0',
    infoBg: '#E6F0FA',
    infoInk: '#1E4E82',
    infoBorder: '#E6F0FA',

    rowHead: '#F7F9FC',
    rowHover: '#FAFBFD',
    rowAlt: '#F9FAFC',
    rowBad: '#FDF1EF',
    rowWarn: '#FFF8EC',
    fieldBorder: '#CBD2DC',
    placeholder: '#9AA5B4',

    scanBg: '#111827',
  },
};

/**
 * The EmeronCore look (ADR-013): the platform's own design system — Inter, slate ink on white and near-white
 * surfaces, hairline borders, a single blue accent, 8 px controls on 12 px cards — worn by this installation as its
 * default. The values are the platform's "Emeron" preset and its runtime tokens (apps/runtime/src/tokens/tokens.css),
 * carried over token for token; the names stay the department's so a theme built for either can be worn by the other.
 */
export const EMERON: Theme = {
  name: 'Emeron',
  dark: false,
  radius: 8,
  fontSize: 14,
  fontFamily: '"Inter Variable", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontFamilyAr: '"Inter Variable", "Segoe UI", Tahoma, "Noto Naskh Arabic", Arial, sans-serif',
  colors: {
    navy: '#0F172A',
    navy2: '#1E293B',
    navy3: '#020617',
    gold: '#2563EB',
    gold2: '#1D4ED8',
    goldHover: '#1D4ED8',
    onGold: '#FFFFFF',

    // White throughout — the canvas, the cards and the sidebar are one sheet of paper, and the structure comes from
    // hairlines and type rather than from panels of grey. A form of mostly read-only fields (a sent purchase order is
    // nearly all read-only) turned into a page of grey boxes under the old tinted canvas; on white the values read as
    // values, and the few fields that can still be edited are the ones that carry a visible box.
    bg: '#FFFFFF',
    paper: '#FFFFFF',
    ink: '#0F172A',
    muted: '#64748B',
    line: '#E6E9EF',
    line2: '#F1F5F9',

    sbBg: '#FFFFFF',
    sbLine: '#E6E9EF',
    sbActive: '#EFF6FF',
    sbText: '#374151',
    sbTextDim: '#9CA3AF',
    sbTextStrong: '#111827',
    sbHeading: '#9CA3AF',
    sbHover: '#F3F4F6',

    svc: '#059669',
    svcBg: '#DCFCE7',
    svcInk: '#166534',
    svcBorder: '#BBF7D0',
    us: '#DC2626',
    usBg: '#FEE2E2',
    usInk: '#991B1B',
    usBorder: '#FECACA',
    quar: '#D97706',
    quarBg: '#FEF9C3',
    quarInk: '#854D0E',
    quarBorder: '#FDE68A',
    goldBg: '#EFF6FF',
    info: '#2563EB',
    infoBg: '#DBEAFE',
    infoInk: '#1E40AF',
    infoBorder: '#BFDBFE',

    rowHead: '#FAFAFA',
    rowHover: '#F0F1F3',
    rowAlt: '#F9FAFB',
    rowBad: '#FEF2F2',
    rowWarn: '#FFFBEB',
    fieldBorder: '#E5E7EB',
    placeholder: '#9CA3AF',

    scanBg: '#0F172A',
  },
};

/** The CSS custom property each colour is published as, so the stylesheet and this file cannot drift apart. */
export const CSS_VARIABLES: Readonly<Record<keyof ThemeColors, string>> = {
  navy: '--navy',
  navy2: '--navy-2',
  navy3: '--navy-3',
  gold: '--gold',
  gold2: '--gold-2',
  goldHover: '--gold-hover',
  onGold: '--on-gold',
  bg: '--bg',
  paper: '--paper',
  ink: '--ink',
  muted: '--muted',
  line: '--line',
  line2: '--line-2',
  sbBg: '--sb-bg',
  sbLine: '--sb-line',
  sbActive: '--sb-active',
  sbText: '--sb-text',
  sbTextDim: '--sb-text-dim',
  sbTextStrong: '--sb-text-strong',
  sbHeading: '--sb-heading',
  sbHover: '--sb-hover',
  svc: '--svc',
  svcBg: '--svc-bg',
  svcInk: '--svc-ink',
  svcBorder: '--svc-border',
  us: '--us',
  usBg: '--us-bg',
  usInk: '--us-ink',
  usBorder: '--us-border',
  quar: '--quar',
  quarBg: '--quar-bg',
  quarInk: '--quar-ink',
  quarBorder: '--quar-border',
  goldBg: '--gold-bg',
  info: '--info',
  infoBg: '--info-bg',
  infoInk: '--info-ink',
  infoBorder: '--info-border',
  rowHead: '--row-head',
  rowHover: '--row-hover',
  rowAlt: '--row-alt',
  rowBad: '--row-bad',
  rowWarn: '--row-warn',
  fieldBorder: '--field-border',
  placeholder: '--placeholder',
  scanBg: '--scan-bg',
};

/** Every colour a theme must carry — the admin editor and the import both check against this. */
export const COLOR_KEYS = Object.keys(CSS_VARIABLES) as (keyof ThemeColors)[];
